import {
    Bill,
    Convert,
    DocumentText,
    Fatrows,
    GlobalEdit,
    Message,
    MessageQuestion,
    ReceiptItem,
    SearchNormal,
    Send2,
    WalletAdd,
} from "iconsax-reactjs";
import React, { useEffect, useState, useRef } from "react";
import { IoChevronDown } from "react-icons/io5";
import emptyTransactionsImg from "../../asset/images/Empty.svg";
import { useSelector } from "react-redux";
import { handleFetchWalletTransactions } from "../../api/fetchers";
import NoMatchingTransaction from "../../asset/images/NoMatchingTransaction.svg";
import FilterMenu from "./FilterMenu";
import Loader from "../loader";
import TransactionDetails from "./TransactionDetails";
import Pagination from "../pagination";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { formatTransactions, groupByDate } from "../../utilities/transactionUtils";
import TransactionDetailsModal from "./TransactionDetailsModal";
import { FiArrowDownLeft, FiArrowUpRight } from "react-icons/fi";
import { logDOM } from "@testing-library/react";
import useDebounce from "../../hooks/useDebounce";
import { safeCurrencyFormat } from "../../utilities/safeCurrencyFormat";
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const TransactionSection = () => {
    const [selectedTxn, setSelectedTxn] = useState(null);
    const [activeTab, setActiveTab] = useState("All");
    const [tabs, setTabs] = useState(["All"]);
    const { business, currency } = useSelector((state) => state);
    const [showMobileDetails, setShowMobileDetails] = useState(false);

    const [page, setPage] = useState(1);
    const limit = 20;
    const [transactions, setTransactions] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const listRef = useRef(null);
    const [showFilter, setShowFilter] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [filters, setFilters] = useState({
        status: [],
        type: [],
        date: { from: null, to: null },
    });

    const debouncedSearch = useDebounce((value) => {
        setSearchTerm(value);
        setPage(1);
    }, 700);

    const useIsMobile = () => {
        const [isMobile, setIsMobile] = useState(false);

        useEffect(() => {
            const updateSize = () => {
                setIsMobile(window.innerWidth < 1024);
            };
            window.addEventListener("resize", updateSize);
            updateSize();
            return () => window.removeEventListener("resize", updateSize);
        }, []);

        return isMobile;
    };
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!business?._id) return;
        (async () => {
            setLoading(true);
            try {
                const payload = await handleFetchWalletTransactions({
                    businessId: business._id,
                    page,
                    limit,
                    status: filters.status,
                    status: filters.status,
                    transactionType: filters.type,
                    fromDate: filters.date?.from,
                    toDate: filters.date?.to,
                    currency,
                    searchTerm: searchTerm || undefined,
                    order: "desc",
                });
                const {
                    data: records = [],
                    totalPages: tp = 1,
                    currentPage: cp = 1,
                    statuses = [],
                } = payload;
                setTransactions(records);
                setTotalPages(tp);
                setPage(cp);
                setTabs(["All", ...statuses]);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [business?._id, page, filters.status, filters.type, filters.date, currency, searchTerm]);
    const filterAnchor = { right: 0, top: 18 };

    const dateCount = [Boolean(filters.date?.from), Boolean(filters.date?.to)].filter(
        Boolean
    ).length;
    const totalBadge = filters.status.length + filters.type.length + dateCount;
    const formatted = formatTransactions(transactions);

    const sections = groupByDate(formatted);

    const typeToIcon = {
        "wallet-funding": <WalletAdd size="18" color="#6F739B" variant="Bulk" />,
        "wallet-conversion": <Convert size="18" color="#6F739B" variant="Bulk" />,
        "local-transfer": <Send2 size="18" color="#6F739B" variant="Bulk" />,
        "cross-border-transfer": <GlobalEdit size="18" color="#6F739B" variant="Bulk" />,
        "invoice-payments": <ReceiptItem size="18" color="#6F739B" variant="Bulk" />,
        "order-payments": <DocumentText size="18" color="#6F739B" variant="Bulk" />,
        "shipment-payments": <Message size="18" color="#6F739B" variant="Bulk" />,
        "bill-payment": <Bill size="18" color="#6F739B" variant="Bulk" />,
        finance: <Fatrows size="18" color="#6F739B" variant="Bulk" />,
        others: <MessageQuestion size="18" color="#6F739B" variant="Bulk" />,
    };

    if (loading) {
        return (
            <div className="text-center py-20">
                <button className=" bg-[#6F00FF] px-4 py-2 rounded-lg">
                    <Loader />
                </button>
            </div>
        );
    }

    const noFiltersApplied =
        filters.status.length === 0 && filters.type.length === 0 && !filters.date;
    const someFilterApplied = !noFiltersApplied;

    if (!loading && transactions.length === 0 && noFiltersApplied) {
        return (
            <div className="text-center py-52 bg-white">
                <img
                    src={emptyTransactionsImg}
                    alt="No transactions"
                    className="mx-auto w-32 mb-3"
                />
                <p className="text-[#101828] font-semibold">No transactions yet</p>
                <p className="text-sm text-[#6F6B7B]">
                    Once you start sending or receiving money, they will show up here.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white">
            <div className="md:px-4 pt-4">
                {/* Tabs – scrollable on mobile */}
                <div className="overflow-x-auto hide-scrollbar">
                    <div className="flex md:gap-3 border-b border-gray-200 w-max min-w-full scroll-snap-x px-2">
                        {["All", "Completed", "Pending", "Reported", "Failed"].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setSelectedTxn(null);
                                    setPage(1);
                                    setFilters((f) => ({
                                        ...f,
                                        status:
                                            tab === "All"
                                                ? []
                                                : tab === "Completed"
                                                ? ["Success"]
                                                : [tab],
                                    }));
                                }}
                                className={`text-sm py-2 px-3 md:py-2 md:px-4 whitespace-nowrap rounded-lg border-b-2 scroll-snap-start ${
                                    activeTab === tab
                                        ? "bg-[#EBE4FF] text-[#6F00FF] font-semibold"
                                        : "border-transparent text-gray-500"
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="relative flex px-2 md:flex-row md:items-center md:justify-between mt-4 gap-3 md:gap-0">
                    <div className="w-full md:w-1/4 px-3 py-2 flex gap-2 text-sm rounded-md bg-[#EFF0FA] focus:outline-none">
                        <SearchNormal size="22" color="#6F739B" />
                        <input
                            type="text"
                            placeholder="Search transactions"
                            className="w-full bg-[#EFF0FA] focus:outline-none"
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                debouncedSearch(e.target.value);
                            }}
                        />
                    </div>

                    <button
                        className="flex items-center gap-2 text-sm font-medium bg-[#F5F4FA] px-4 py-2 rounded-xl text-[#101828] relative"
                        onClick={() => setShowFilter((v) => !v)}
                    >
                        {totalBadge > 0 && (
                            <span className="bg-[#6F00FF] text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                {totalBadge}
                            </span>
                        )}
                        Filter <IoChevronDown size={16} className="text-[#6F6B7B]" />
                    </button>

                    <FilterMenu
                        show={showFilter}
                        onClose={() => setShowFilter(false)}
                        activeFilter={activeFilter}
                        setActiveFilter={setActiveFilter}
                        filters={filters}
                        setFilters={setFilters}
                        typeToIcon={typeToIcon}
                        anchorOffset={filterAnchor}
                    />
                </div>
            </div>

            {!loading && transactions.length === 0 && someFilterApplied ? (
                <div className="text-center py-28 bg-white">
                    <img
                        src={NoMatchingTransaction}
                        alt="No matching transactions"
                        className="mx-auto w-32 mb-4"
                    />
                    <h2 className="text-lg font-semibold text-[#101828]">
                        No matching transactions
                    </h2>
                    <p className="text-sm text-[#6F6B7B] mt-2">
                        We couldn’t find any transactions that match your filter or search.
                    </p>
                </div>
            ) : (
                <div className="bg-white px-2 rounded-xl flex flex-col lg:flex-row max-h-[100vh] overflow-hidden">
                    {/* Left Pane */}
                    <div className="lg:w-1/2 border-r border-[#E4E4E7] md:px-4 pt-4">
                        <div className="hide-scrollbar space-y-6 max-h-[80vh] overflow-auto">
                            {[...sections.entries()].map(([sectionLabel, txns]) => (
                                <div key={sectionLabel}>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                                        {sectionLabel}
                                    </h3>
                                    <div className="space-y-3">
                                        {txns.map((txn) => (
                                            <div
                                                key={txn.id}
                                                className={`flex justify-between items-center px-4 py-3 rounded-lg cursor-pointer transition ${
                                                    selectedTxn?.id === txn.id
                                                        ? "bg-[#FBFAFF] border-1 border-solid !border-[#6F00FF]"
                                                        : "bg-white border border-transparent hover:border-[#E0D4E7]"
                                                }`}
                                                onClick={() => {
                                                    setSelectedTxn(txn);
                                                    if (isMobile) setShowMobileDetails(true);
                                                }}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="relative w-9 h-9">
                                                        <div className="w-full h-full bg-[#F2F3FC] rounded-full flex items-center justify-center">
                                                            {typeToIcon[txn.transactionType] || (
                                                                <MessageQuestion
                                                                    size="18"
                                                                    color="#6F739B"
                                                                    variant="Bulk"
                                                                />
                                                            )}
                                                        </div>

                                                        {txn.paymentType === "credit" && (
                                                            <span
                                                                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center
               bg-[#F2F3FC] ring-2 ring-white"
                                                            >
                                                                <FiArrowDownLeft
                                                                    size="12"
                                                                    color="#101828"
                                                                />
                                                            </span>
                                                        )}

                                                        {txn.paymentType === "debit" && (
                                                            <span
                                                                className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center
               bg-[#F2F3FC] ring-2 ring-white"
                                                            >
                                                                <FiArrowUpRight
                                                                    size="12"
                                                                    color="#101828"
                                                                />
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="max-w-xs break-words">
                                                        <p className="text-sm font-medium text-[#101828]">
                                                            {txn.title}
                                                        </p>
                                                        <p className="text-xs text-[#6F6B7B]">
                                                            {dayjs(txn.time).format(
                                                                "MMM D, YYYY • h:mm A"
                                                            )}{" "}
                                                            •{" "}
                                                            <span
                                                                className={
                                                                    txn.status === "Completed"
                                                                        ? "text-[#188B18]"
                                                                        : txn.status === "Pending" || txn.status === "Proccessing"
                                                                        ? "text-orange-500"
                                                                        : "text-red-500"
                                                                }
                                                            >
                                                                {txn.status}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <div
                                                    className={`text-sm font-semibold ${
                                                        txn.amount >= 0
                                                            ? "text-[#188B18]"
                                                            : "text-[#161827]"
                                                    }`}
                                                >
                                                    {txn.amount < 0 && "-"}
                                                    {safeCurrencyFormat(
                                                        Math.abs(txn.amount),
                                                        txn.currency
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* pagination */}
                        <Pagination
                            currentPage={page}
                            setCurrentPage={setPage}
                            totalPages={totalPages}
                        />

                        <TransactionDetailsModal
                            isOpen={showMobileDetails}
                            txn={selectedTxn}
                            onClose={() => setShowMobileDetails(false)}
                            typeToIcon={typeToIcon}
                        />
                    </div>

                    {!isMobile && (
                        <div className="lg:w-1/2">
                            <TransactionDetails txn={selectedTxn} typeToIcon={typeToIcon} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TransactionSection;
